<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

  <div class="container mt-4 mb-4">
    <div style="max-width:620px">
        <h3>Unimicro</h3>

        <div v-if="locations.length === 0 && !loading" class="mt-4">
          <span class="message-info">Ingen avdelinger har Unimicro som regnskapssystem. Velg det under Avdelinger.</span>
        </div>

        <div v-if="locations.length > 0 && hasMultiLocations()" class="select-location-box mt-4">
          <span class="ingredient-title">For avdeling</span>
          <select class="form-select" v-model="locationId" @change="load()">
            <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
          </select>
        </div>

        <template v-if="locations.length > 0 && status">
          <div class="mt-3 d-flex align-items-center gap-2 flex-wrap">
            <span class="ingredient-title mb-0">Status</span>
            <span v-if="!status.configured" class="badge bg-danger">CAGISTA er ikke satt opp mot Unimicro</span>
            <span v-else-if="status.purchaseStatus === 'ACTIVE'" class="badge bg-success">Aktiv</span>
            <span v-else-if="status.purchaseStatus === 'PENDING'" class="badge bg-warning text-dark">Aktivert – venter på fullføring</span>
            <span v-else-if="status.purchaseStatus === 'CANCELED'" class="badge bg-secondary">Avsluttet</span>
            <span v-else class="badge bg-secondary">Ikke aktivert</span>
            <span class="badge" :class="status.environment === 'PROD' ? 'bg-dark' : 'bg-info text-dark'">{{ status.environment === 'PROD' ? 'Produksjon' : 'Testmiljø' }}</span>
          </div>

          <!-- 1. Nothing yet, or ended: how the merchant grants access (again) -->
          <div v-if="!status.companyKeySet || status.purchaseStatus === 'CANCELED'" class="mt-3">
            <!-- Ended: who ended it and when. Without this «Avsluttet» read exactly like a location that was
                 never activated, and the SMS about a deactivation led to a page that did not mention it. -->
            <template v-if="status.purchaseStatus === 'CANCELED' && status.canceledAt">
              <p v-if="status.canceledBy === 'UNIMICRO'">
                <strong>Avsluttet i Unimicro {{ formatDateTime(status.canceledAt) }}.</strong>
                {{ status.companyName || 'Selskapet' }} deaktiverte CAGISTA i Unimicro, og bokføringen for
                denne avdelingen er stoppet.
              </p>
              <p v-else-if="status.canceledBy === 'CAGISTA'">
                <strong>Avsluttet fra CAGISTA {{ formatDateTime(status.canceledAt) }}</strong> med
                «Avslutt integrasjonen». Bokføringen er stoppet, og Unimicro er bedt om å fjerne tilgangen.
              </p>
              <p v-else-if="status.canceledBy === 'MANUAL'">
                <strong>Satt som avsluttet manuelt {{ formatDateTime(status.canceledAt) }}</strong><span
                  v-if="status.statusSetManuallyBy"> av {{ status.statusSetManuallyBy }}</span>. Unimicro er ikke
                varslet, så integrasjonen kan fortsatt stå som aktiv der.
              </p>
            </template>
            <p>
              Tilgangen gis fra <strong>Unimicro</strong>, ikke herfra.
              {{ status.purchaseStatus === 'CANCELED' ? 'Slik slår dere den på igjen:' : 'Slik gjør dere det:' }}
            </p>
            <ol>
              <li>Logg inn i Unimicro ({{ status.environment === 'PROD' ? 'app.unimicro.no' : 'test.unimicro.no' }}) på selskapet med orgnr <strong>{{ orgNumber || '…' }}</strong>.</li>
              <li>Gå til <strong>Markedsplass</strong>, finn <strong>CAGISTA</strong> og trykk <strong>Aktiver integrasjon</strong>.</li>
              <li>Kom tilbake hit og trykk <strong>Sjekk status</strong>. Unimicro gir oss beskjed automatisk, men knappen henter det om beskjeden ikke har kommet.</li>
            </ol>
            <p class="small" style="color:var(--bo-muted)">
              Unimicro oppretter da en serverbruker for CAGISTA på selskapet.
            </p>
            <button type="button" class="bo_btn" :disabled="working || !status.configured" @click="activate">{{ working ? 'Sjekker…' : 'Sjekk status' }}</button>
          </div>

          <!-- 2. Activated in the marketplace, not yet finished here -->
          <div v-else-if="status.purchaseStatus === 'PENDING'" class="mt-3">
            <p>
              <!-- No whitespace between «Unimicro» and the optional parts: Unimicro sends no Email on the event
                   (TEST 2026-10-05), and a hidden span after a line break left «i Unimicro .» on screen.
                   The time is always known, so it is shown on its own. -->
              <strong>{{ status.companyName }}</strong> har aktivert CAGISTA i Unimicro<template
                v-if="status.activatedAt"> {{ formatDateTime(status.activatedAt) }}</template><span
                v-if="status.activatedBy"> ({{ status.activatedBy }})</span>.
              Bokføringen starter ikke før oppsettet er fullført:
            </p>
            <!-- «Fullfør aktivering» comes FIRST. While the purchase is «Avventer» Unimicro gives our server
                 user no access to the company (TEST 2026-10-05), so the department list in the settings is
                 empty and «Test tilkobling» can only fail — which is also why that button is not offered here. -->
            <ol>
              <li>Trykk <strong>Fullfør aktivering</strong>. Da blir integrasjonen aktiv hos Unimicro, og først da gir Unimicro oss tilgang til selskapet.</li>
              <li>Sett så kontoer og avdeling under <a href="/unimicroAccountSettings">Unimicro-innstillinger</a>. Avdelingslisten hentes fra Unimicro og er tom før steg 1.</li>
              <li>Slå på automatisk synkronisering når kontoene stemmer. Ingenting bokføres før det er slått på.</li>
            </ol>
            <div class="d-flex gap-3 flex-wrap">
              <button type="button" class="bo_btn" :disabled="working" @click="activate">{{ working ? 'Fullfører…' : 'Fullfør aktivering' }}</button>
              <button type="button" class="bo_btn_secondary" :disabled="working" @click="disconnect">Avslutt integrasjonen</button>
            </div>
          </div>

          <!-- 3. Active -->
          <div v-else class="mt-3">
            <p>
              Koblet til <strong>{{ status.companyName }}</strong>. Z-rapporter bokføres automatisk hver natt
              når det er slått på under <a href="/unimicroAccountSettings">Unimicro-innstillinger</a>.
            </p>
            <div class="d-flex gap-3 flex-wrap">
              <button type="button" class="bo_btn_secondary" :disabled="working" @click="testConnection">Test tilkobling</button>
              <button type="button" class="bo_btn_secondary" :disabled="working" @click="disconnect">Avslutt integrasjonen</button>
            </div>
          </div>

          <!--
            The manual override. Unimicro and we can disagree about the purchase status, and only our
            side is ours to fix — a stuck PENDING stops the nightly job for a merchant who has done
            nothing wrong. This was being corrected by editing the document in Couchbase.
            It does NOT tell Unimicro; «Fullfør aktivering» is the honest path and is right above.
          -->
          <div v-if="isSuperAdmin && status.companyKeySet" class="mt-5 override-box">
            <div class="override-title">Overstyr status (SUPER_ADMIN)</div>
            <p class="small mb-2" style="color:var(--bo-muted)">
              Setter statusen bare hos oss — Unimicro får ingen beskjed. Bruk «Fullfør aktivering»
              over hvis den virker; dette er for når de to sidene har kommet ut av takt.
              Nå: <strong>{{ status.purchaseStatus || 'ingen' }}</strong>.
            </p>
            <button type="button" class="bo_btn_secondary bo_btn_compact me-2" :disabled="working"
                    @click="overrideStatus('ACTIVE')">Sett som aktiv</button>
            <button type="button" class="bo_btn_secondary bo_btn_compact me-2" :disabled="working"
                    @click="overrideStatus('PENDING')">Sett som venter</button>
            <button type="button" class="bo_btn_secondary bo_btn_compact" :disabled="working"
                    @click="overrideStatus('CANCELED')">Sett som avsluttet</button>
          </div>

          <div v-if="message" class="mt-3 mb-5">
            <span :class="messageOk ? 'message-success' : 'message-error'">{{ message }}</span>
          </div>
        </template>
    </div>
  </div>
</template>

<script>
  import SettingsService from '@/services/settings/SettingsService';
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
  import LocationUtil from "@/util/LocationUtil";
  import UserUtil from "@/util/UserUtil";
  import LocationService from "@/services/location/LocationService";
  import moment from "moment";

  /**
   * The Unimicro page after the 2026-09-22 rebuild: no credentials to type and no «Koble til» redirect.
   * The merchant activates CAGISTA in Unimicro's marketplace; this page shows where that stands for the
   * location and finishes it («Fullfør aktivering» → the purchase is set active at Unimicro).
   */
  export default {
    components: {LoadingSpinner, ConfirmDialogue},
    data() {
      return {
        loading: true,
        working: false,
        locationId: '',
        locations: [],
        status: null,
        message: '',
        messageOk: false
      };
    },
    computed: {
      isSuperAdmin(){ return UserUtil.isSuperAdmin(); },
      orgNumber(){
        const location = this.locations.find(l => l.id === this.locationId);
        return location && location.vatNumber ? location.vatNumber : '';
      }
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'UNIMICRO');
            this.locationId = this.locations.length ? this.locations[0].id : '';
            if (this.locationId) {
              this.load();
            } else {
              this.loading = false;
            }
          },
          (error) => { console.log(error); this.loading = false; }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      formatDate(iso){
        return iso ? moment(iso).format('DD.MM.YYYY HH:mm') : '';
      },
      formatDateTime(iso){
        return iso ? moment(iso).format('DD.MM.YYYY [kl.] HH:mm') : '';
      },
      /*
       * Deliberately does NOT clear `message`. activate() and disconnect() both set a result and then
       * call this to refresh the status — so clearing here wiped the very message the click produced,
       * and a failed «Fullfør aktivering» looked like a button that did nothing at all. Starting a new
       * operation is what clears the message, and each of those already does it.
       */
      load(){
        this.loading = true;
        SettingsService.getUnimicroStatus(this.locationId).then(
            (response) => {
              this.status = response.data;
              this.loading = false;
            },
            (error) => { console.log(error); this.loading = false; }
        );
      },
      /*
       * Sets the status on our side only. Confirmed for ACTIVE because that is the one that starts
       * the 04:15 job booking into a real ledger; the other two only stop things.
       */
      async overrideStatus(status){
        if(status === 'ACTIVE'){
          const ok = await this.$refs.confirmDialogue.show({
            title: 'Sett som aktiv',
            message: 'Statusen settes til ACTIVE bare hos oss. Unimicro får ingen beskjed, og nattlig '
                   + 'synkronisering vil begynne å bokføre for denne avdelingen.',
            okButton: 'Sett som aktiv',
            cancelButton: 'Avbryt'
          });
          if(!ok) return;
        }
        this.working = true;
        this.message = '';
        SettingsService.setUnimicroPurchaseStatus(this.locationId, status).then(
            (response) => {
              this.working = false;
              this.message = response.data.message;
              this.messageOk = !!response.data.success;
              this.load();
            },
            (error) => { console.log(error); this.working = false; this.message = 'Noe gikk galt.'; this.messageOk = false; }
        );
      },
      activate(){
        this.working = true;
        this.message = '';
        SettingsService.activateUnimicro(this.locationId).then(
            (response) => {
              this.working = false;
              this.message = response.data.message;
              this.messageOk = !!response.data.success;
              this.load();
            },
            (error) => { console.log(error); this.working = false; this.message = 'Noe gikk galt.'; this.messageOk = false; }
        );
      },
      testConnection(){
        this.working = true;
        this.message = '';
        SettingsService.testUnimicroConnection(this.locationId).then(
            (response) => {
              this.working = false;
              this.message = response.data.message;
              this.messageOk = !!response.data.success;
            },
            (error) => { console.log(error); this.working = false; this.message = 'Feil ved tilkobling'; this.messageOk = false; }
        );
      },
      async disconnect(){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Avslutt Unimicro-integrasjonen',
          message: 'Integrasjonen settes som avsluttet hos Unimicro, og synkroniseringen stopper. '
              + 'Vil dere ha den tilbake, aktiverer dere CAGISTA på nytt i Unimicro → Markedsplass.',
          okButton: 'Avslutt',
          cancelButton: 'Avbryt'
        });
        if(!ok) return;
        this.working = true;
        this.message = '';
        SettingsService.disconnectUnimicro(this.locationId).then(
            () => { this.working = false; this.load(); },
            (error) => { console.log(error); this.working = false; }
        );
      }
    }
  };
</script>

<style scoped>
  /* Set apart from the merchant-facing steps above it: this is the escape hatch, not the flow. */
  .override-box {
    border: 1px dashed var(--bo-line);
    border-radius: 8px;
    padding: 14px 16px;
    max-width: 640px;
  }

  .override-title {
    font-weight: bold;
    margin-bottom: 4px;
  }
</style>
